'use client';

import { useOptimizely } from 'providers/OptimizelyProvider';
import { createContext, useContext, useEffect, useState } from 'react';
import { Breakpoints, useMediaQuery } from '@/app_lib/hooks/useMediaQuery';
import { useAdContext } from '@/app_lib/providers/AdProvider';

interface StickyFooterProviderProps {
  children: React.ReactNode;
}

interface StickyFooterContextInterface {
  renderStickyFooter: boolean;
}

const defaultStickyFooterContext: StickyFooterContextInterface = {
  renderStickyFooter: false,
};

// biome-ignore lint/style/useComponentExportOnlyModules: context should be with provider
export const StickyFooterContext = createContext<StickyFooterContextInterface>(defaultStickyFooterContext);

export const StickyFooterProvider = ({ children }: StickyFooterProviderProps) => {
  const [renderStickyFooter, setRenderStickyFooter] = useState(defaultStickyFooterContext.renderStickyFooter);

  const { pageAdConfig } = useAdContext();
  const { userFlags: userVariants } = useOptimizely();
  const isMobile = useMediaQuery(Breakpoints.Mobile) ?? false;

  useEffect(() => {
    if (!pageAdConfig) {
      return;
    }

    const { articleStickyFooter } = userVariants as { articleStickyFooter: { enabled: boolean } };
    const isArticle = pageAdConfig?.ctype === 'article-v2';

    setRenderStickyFooter(isArticle && isMobile && articleStickyFooter?.enabled);
  }, [pageAdConfig, userVariants, isMobile]);

  return <StickyFooterContext.Provider value={{ renderStickyFooter }}>{children}</StickyFooterContext.Provider>;
};

// biome-ignore lint/style/useComponentExportOnlyModules: context should be with provider
export const useStickyFooter = () => {
  const context = useContext(StickyFooterContext);
  if (context === undefined) {
    throw new Error('useStickyFooter must be used within a StickyFooterProvider');
  }
  return context;
};
